import { generateRelativeUrl, readBookingTokenFromUrl } from '../../utils/common';
import { cookieCountry, cookieLanguage } from '../../utils/globalization/marketCookies';
import { LDUser } from '../../utils/ldUser';
import getBrand from '../../utils/getBrand';
import { LOCAL_BFF_URI } from '../../utils/constants';
import { readStoredBookingToken } from '../sessionStorage/utils';
import { currentSearch } from '../../utils/searchTelemetry';

// Same-origin is the default: the page host is what selects the tier (stage2/stage1 for lower,
// new.* for preprod, www.* for production) and getBrand() reads the brand off that same host, so
// one build serves every tier and both brands. The local BFF port is a fallback for non-production
// builds only — a deployed build must never resolve to localhost because the variable is missing.
function configuredBffUri() {
  const value = process.env.REACT_APP_BFF_URI;
  if (value === undefined) return process.env.NODE_ENV === 'production' ? '' : LOCAL_BFF_URI;
  // GitHub Actions writes environment `vars` into the build env verbatim, so a value typed with
  // quotes in the console arrives as those literal characters and would append them to every URL.
  return value.trim().replace(/^(["'])(.*)\1$/, '$2');
}

const BASE_URL = generateRelativeUrl(configuredBffUri());

// Deep links carry the booking token as ?token=..., read before anything lands in session
// storage. This used to be `substring(7)`, replicated verbatim from the legacy stack — quirks
// included, and the quirk mattered: it never decoded, so a percent-encoded token was sent on as
// the cipher and Manage's /v1/bookings/locale answered 500. See readBookingTokenFromUrl.
function getUrlBookingToken() {
  return readBookingTokenFromUrl();
}

// Only on search paths: the series is a property of the search, and tagging every call made while
// one is in flight would label an unrelated profile read as attempt 2.
function searchHeaders(path) {
  const search = currentSearch();
  if (!search || !path.includes('search')) return undefined;
  return { 'x-search-id': search.id, 'x-search-attempt': String(search.attempt) };
}

// `withoutBookingToken` exists because this token is ambient: it is read from session storage on
// every call, so a tab that has already opened one booking keeps sending that token forever. On a
// lookup where the guest has typed a different reservation number, sending it made the BFF answer
// with the PREVIOUS booking — the identifiers were discarded and another guest's name, email and
// price came back. Calls that carry their own explicit identity opt out.
function buildHeaders(extraHeaders, { withoutBookingToken = false } = {}) {
  const bookingToken = withoutBookingToken
    ? undefined
    : readStoredBookingToken() || getUrlBookingToken();

  const headers = {
    'Content-Type': 'application/json',
    country: cookieCountry().toUpperCase(),
    language: cookieLanguage(),
    // The BFF and every upstream behind it (cruise-search, Manage, ESL Guest Account) take the
    // brand as an 'R'/'C' header and default to Royal when absent. The deployed legacy relied on
    // the edge injecting it per domain — the client must send it itself for Celebrity to get
    // Celebrity ships/sailings/bookings anywhere the edge isn't in front (local, direct BFF).
    brand: getBrand().id === 'cel' ? 'C' : 'R',
    'x-session-id': LDUser(),
    ...(bookingToken && { 'x-booking-token': bookingToken }),
    ...extraHeaders,
  };

  return Object.fromEntries(Object.entries(headers).filter(([, value]) => value != null));
}

function buildQueryString(params) {
  if (!params) return '';
  const query = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
  return query ? `?${query}` : '';
}

// Mirrors just enough of ApolloError's shape (graphQLErrors[].extensions.code) for
// utils/common.js's findErrorCode/findErrorMessage to keep working unchanged against a
// REST-sourced error, since several components branch on specific upstream error codes.
function normalizeError({ networkError, status, body }) {
  // A real Error instance, not a plain object: several components branch on
  // String(error).includes(...) — e.g. FlightExpanded's hold-error navigation matches
  // 'Re-hold is not allowed'/'ACE-AB-1210' — which relies on Error's toString; a plain
  // object stringifies to '[object Object]' and every error would fall through to the
  // generic error page.
  if (networkError) {
    const error = new Error(networkError.message);
    error.graphQLErrors = [];
    error.networkError = networkError;
    return error;
  }
  const message = body?.userMessage || body?.error || 'Unexpected error';
  const error = new Error(message);
  // Always a status-prefixed string: the legacy RESTDataSource surfaced upstream failures as
  // '504: ...' GraphQL error messages and Results/BrowseResults branch their search-retry UI on
  // message.startsWith('504') — and a gateway error body may not be JSON at all (body undefined),
  // which would otherwise leave message undefined and crash those branches.
  error.graphQLErrors = [
    {
      message: `${status}: ${body?.error ?? message}`,
      extensions: {
        code: body?.code,
        statusCode: status,
        // findErrorMessage matches on extensions.exception.message (the legacy upstream
        // exception text, e.g. BookFlights' 'BOOKING IS LOCKED' branch) — mirror the most
        // upstream-flavored text available.
        exception: { message: body?.userMessage ?? body?.error ?? message },
      },
    },
  ];
  error.networkError = null;
  return error;
}

// With the API's aceRejectionsAs200 on, ACE refusing a flight request (no flights for the search, a
// date out of range, a re-hold not allowed) arrives as a 200 whose body is the very error the 502
// used to carry: { error, statusCode: 502, code, userMessage }. Read here, at the one place every
// call passes through, so everything downstream — findErrorCode, the ACE-AB-1210 hold navigation,
// the no-flights banner, the search retry that a business answer cancels — keeps receiving the same
// error object it always did. Limited to the flights routes and to a body that is unmistakably an
// error (a message and a status of 400 or more): a search result, or cancel's own
// { status: 'FAILED' } answer, never looks like that. A 502 from an API without the flag takes the
// !response.ok path below, unchanged.
const FLIGHTS_PATH = '/air-api/v1/flights/';
function isRefusalBody(path, body) {
  return (
    path.startsWith(FLIGHTS_PATH) &&
    typeof body?.error === 'string' &&
    Number.isInteger(body.statusCode) &&
    body.statusCode >= 400
  );
}

// `signal` is forwarded, never created here: fetch has no timeout of its own, so a hung connection
// never rejects — but the deadline belongs to whoever knows the budget. The globalization provider
// owns one for its whole startup; flight searches deliberately pass none, since GDS responses
// routinely take 30-45s and the retry timers own that behaviour.
export default async function restRequest({
  method = 'GET',
  path,
  params,
  body,
  headers,
  signal,
  withoutBookingToken = false,
} = {}) {
  const url = `${BASE_URL}${path}${buildQueryString(params)}`;

  let response;
  try {
    response = await fetch(url, {
      method,
      headers: buildHeaders({ ...searchHeaders(path), ...headers }, { withoutBookingToken }),
      body: body ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (networkError) {
    throw normalizeError({ networkError });
  }

  const responseBody = await response.json().catch(() => undefined);

  if (!response.ok) {
    throw normalizeError({ status: response.status, body: responseBody });
  }

  if (isRefusalBody(path, responseBody)) {
    throw normalizeError({ status: responseBody.statusCode, body: responseBody });
  }

  return responseBody;
}
